import { Audio, AudioListener, AudioLoader } from 'three';

export class NarrationAudio {
  constructor(camera, manager) {
    this.listener = new AudioListener();
    this.sound = new Audio(this.listener);
    this.ambientSound = new Audio(this.listener);
    this.loader = new AudioLoader(manager);
    this.currentUrl = null;
    this.currentUtterance = null;
    this.mode = null;
    this.loadToken = 0;
    this.state = 'idle';
    this.onStateChange = null;
    this.ambientUrl = null;
    this.ambientLoadToken = 0;
    camera.add(this.listener);

    this.sound.onEnded = () => {
      this.setState('stopped');
    };
  }

  setState(state) {
    this.state = state;
    this.onStateChange?.(state);
  }

  async play(url, hotspotData = null) {
    if (!url && hotspotData?.narration) {
      return this.playSpeech(hotspotData.narration);
    }
    if (!url) {
      this.setState('unavailable');
      return false;
    }

    if (this.listener.context.state === 'suspended') {
      await this.listener.context.resume();
    }

    if (this.currentUrl === url && this.sound.buffer) {
      if (!this.sound.isPlaying) this.sound.play();
      this.mode = 'buffer';
      this.setState('playing');
      return true;
    }

    this.stop();
    this.currentUrl = url;
    this.mode = 'buffer';
    const token = ++this.loadToken;
    this.setState('loading');

    try {
      const buffer = await this.loader.loadAsync(url);
      if (token !== this.loadToken) return false;
      this.sound.setBuffer(buffer);
      this.sound.setLoop(false);
      this.sound.setVolume(0.9);
      this.sound.play();
      this.setState('playing');
      return true;
    } catch (error) {
      if (token === this.loadToken) {
        console.error(`No se ha podido cargar el audio: ${url}`, error);
        this.currentUrl = null;
        this.mode = null;
        this.setState('error');
      }
      return false;
    }
  }

  playSpeech(text) {
    if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) {
      this.setState('unavailable');
      return false;
    }

    this.stop();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'es-ES';
    utterance.rate = 0.94;
    utterance.pitch = 1;
    utterance.onend = () => {
      if (this.currentUtterance === utterance) this.setState('stopped');
    };
    utterance.onerror = (event) => {
      if (event.error === 'canceled' || event.error === 'interrupted') return;
      console.error('No se ha podido sintetizar la narración.', event.error);
      if (this.currentUtterance === utterance) this.setState('error');
    };
    this.currentUtterance = utterance;
    this.mode = 'speech';
    window.speechSynthesis.speak(utterance);
    this.setState('playing');
    return true;
  }

  pause() {
    if (this.mode === 'speech') {
      window.speechSynthesis.pause();
      this.setState('paused');
      return;
    }
    if (!this.sound.isPlaying) return;
    this.sound.pause();
    this.setState('paused');
  }

  resume() {
    if (this.mode === 'speech') {
      window.speechSynthesis.resume();
      this.setState('playing');
      return;
    }
    if (!this.sound.buffer || this.sound.isPlaying) return;
    this.sound.play();
    this.setState('playing');
  }

  stop() {
    this.loadToken += 1;
    if (this.mode === 'speech' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    if (this.sound.isPlaying) this.sound.stop();
    this.currentUrl = null;
    this.currentUtterance = null;
    this.mode = null;
    this.setState('stopped');
  }

  async playAmbient(url, { loop = true, volume = 0.22 } = {}) {
    if (!url) return false;
    if (this.listener.context.state === 'suspended') {
      await this.listener.context.resume();
    }

    if (this.ambientUrl === url && this.ambientSound.buffer) {
      if (!this.ambientSound.isPlaying) this.ambientSound.play();
      return true;
    }

    this.stopAmbient();
    const token = ++this.ambientLoadToken;
    try {
      const buffer = await this.loader.loadAsync(url);
      if (token !== this.ambientLoadToken) return false;
      this.ambientSound.setBuffer(buffer);
      this.ambientSound.setLoop(loop);
      this.ambientSound.setVolume(volume);
      this.ambientSound.play();
      this.ambientUrl = url;
      return true;
    } catch (error) {
      console.error(`No se ha podido cargar el audio ambiental: ${url}`, error);
      return false;
    }
  }

  pauseAmbient() {
    if (this.ambientSound.isPlaying) this.ambientSound.pause();
  }

  stopAmbient() {
    this.ambientLoadToken += 1;
    if (this.ambientSound.isPlaying) this.ambientSound.stop();
    this.ambientUrl = null;
  }

  dispose() {
    this.stop();
    this.stopAmbient();
    this.listener.parent?.remove(this.listener);
  }
}
